import { type RefObject, useEffect, useRef } from 'react';

import { useUser } from '@clerk/react';
import type { UserResource } from '@clerk/shared/types';
import { ClerkMetadataKey } from '@this+that/utils';
import log from 'loglevel';

import { useTeamStore } from '../../store/Team/useTeamStore';
import { updateUnsafeMetadata } from '../../utils/clerkMetadata/updateUnsafeMetadata';

const ACTIVE_TEAM_PERSIST_DEBOUNCE_MS = 500;

function resolveStoredActiveTeamId(
  unsafeMetadata: Record<string, unknown> | undefined,
): string | null {
  const stored = unsafeMetadata?.[ClerkMetadataKey.ACTIVE_TEAM_ID];
  return typeof stored === 'string' && stored.length > 0 ? stored : null;
}

function hydrateActiveTeamIdFromMetadata(storedId: string): void {
  const { activeTeamId, userTeamIds, setActiveTeamId } =
    useTeamStore.getState();

  if (storedId === activeTeamId) {
    return;
  }

  // userTeamIds still loading — accept it now; setUserTeamIds's parity clamp
  // corrects it once membership is known if the user is no longer a member.
  if (userTeamIds === null || userTeamIds.includes(storedId)) {
    setActiveTeamId(storedId);
  }
}

function useHydrateActiveTeamId(user: UserResource | null | undefined): void {
  useEffect(() => {
    if (!user?.id) {
      return;
    }
    const storedId = resolveStoredActiveTeamId(
      user.unsafeMetadata as Record<string, unknown> | undefined,
    );
    if (storedId) {
      hydrateActiveTeamIdFromMetadata(storedId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);
}

function shouldPersistActiveTeamId(
  activeTeamId: string | null,
  userTeamIds: string[] | null,
): activeTeamId is string {
  return (
    activeTeamId !== null && (userTeamIds?.includes(activeTeamId) ?? false)
  );
}

function persistActiveTeamId(
  user: UserResource | null | undefined,
  activeTeamId: string,
): void {
  if (!user) {
    return;
  }
  updateUnsafeMetadata(user, {
    [ClerkMetadataKey.ACTIVE_TEAM_ID]: activeTeamId,
  }).catch((error: unknown) =>
    log.error('Failed to persist active team', { error }),
  );
}

function useDebouncedActiveTeamPersistence(
  userRef: RefObject<UserResource | null | undefined>,
): void {
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const flush = () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        const { activeTeamId, userTeamIds } = useTeamStore.getState();
        if (!shouldPersistActiveTeamId(activeTeamId, userTeamIds)) {
          return;
        }
        const metadata = userRef.current?.unsafeMetadata as
          | Record<string, unknown>
          | undefined;
        if (resolveStoredActiveTeamId(metadata) === activeTeamId) {
          return;
        }
        persistActiveTeamId(userRef.current, activeTeamId);
      }, ACTIVE_TEAM_PERSIST_DEBOUNCE_MS);
    };

    const unsubscribe = useTeamStore.subscribe((state, prevState) => {
      if (state.activeTeamId !== prevState.activeTeamId) {
        flush();
      }
    });

    return () => {
      unsubscribe();
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [userRef]);
}

export function useActiveTeamClerkSync(): void {
  const { user } = useUser();
  const userRef = useRef(user);
  userRef.current = user;

  useHydrateActiveTeamId(user);
  useDebouncedActiveTeamPersistence(userRef);
}
